import { AlertCircle, RefreshCw } from "lucide-react";
import type { ReactNode } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import { RequireAccess } from "@/app/auth/RequireAccess";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import useTranslations from "@/shared/lang/useTranslations";

import { useOtcAccess } from "./hooks/useOtcAccess";
import CryptoSellOrderPage from "./pages/CryptoSellOrderPage";
import CryptoSellPage from "./pages/CryptoSellPage";
import CryptoSwapHistoryPage from "./pages/CryptoSwapHistoryPage";
import CryptoSwapOrderPage from "./pages/CryptoSwapOrderPage";
import CryptoSwapsPage from "./pages/CryptoSwapsPage";

/**
 * Buying and selling open with the organization's Crypto switch; its history and the detail of past orders
 * and sales stay readable without it. The registry's `enabled` flag only hides the menu, so the switch is
 * enforced here too.
 */
function RequireOtc({ children }: { children: ReactNode }) {
  const { enabled, isLoading, hasError, retry } = useOtcAccess();
  const { t } = useTranslations("cryptoSwaps");
  if (isLoading) return <ColocaSkeleton className="m-2 h-40 w-full max-w-[1400px] rounded-2xl" />;
  if (hasError) {
    return (
      <div className="m-2 max-w-[1400px]">
        <ColocaAlert
          variant="destructive"
          icon={AlertCircle}
          text={
            <span className="flex flex-wrap items-center gap-3">
              {t("access_check_failed")}
              <ColocaButton variant="default" size="xs" icon={RefreshCw} onClick={retry}>
                {t("access_check_retry")}
              </ColocaButton>
            </span>
          }
        />
      </div>
    );
  }
  if (!enabled) return <Navigate to="/app/crypto-swaps/history" replace />;
  return children;
}

/**
 * Five surfaces under one mounted route. The parent carries `RequireRouteAccess` (ANY of them) and each leaf
 * re-checks its own entry, so entering through one surface never grants the others.
 */
export const CryptoSwapsNavigation = () => {
  return (
    <Routes>
      <Route
        path="/"
        element={
          <RequireOtc>
            <RequireAccess to="/app/crypto-swaps">
              <CryptoSwapsPage />
            </RequireAccess>
          </RequireOtc>
        }
      />
      <Route
        path="sell"
        element={
          <RequireOtc>
            <RequireAccess to="/app/crypto-swaps/sell">
              <CryptoSellPage />
            </RequireAccess>
          </RequireOtc>
        }
      />
      <Route
        path="history"
        element={
          <RequireAccess to="/app/crypto-swaps/history">
            <CryptoSwapHistoryPage />
          </RequireAccess>
        }
      />
      <Route
        path="orders/:orderId"
        element={
          <RequireAccess to="/app/crypto-swaps/orders/:orderId">
            <CryptoSwapOrderPage />
          </RequireAccess>
        }
      />
      <Route
        path="sales/:saleId"
        element={
          <RequireAccess to="/app/crypto-swaps/sales/:saleId">
            <CryptoSellOrderPage />
          </RequireAccess>
        }
      />
      <Route path="*" element={<Navigate to="/app/crypto-swaps" replace />} />
    </Routes>
  );
};
